/* eslint-disable */
import EntityNameEditButton from '@/components/onboarding_components/EntityNameEditButton/EntityNameEditButton.vue';
import {
  ACTIVATION_BADGE_CLASSES,
  get_journey_dot_style,
  get_mapped_value,
  PROGRESS_COLORS,
  resolve_case_risk_tone,
  resolve_case_risk_label,
  resolve_case_sla_label,
  resolve_case_sla_tone,
  RISK_BADGE_CLASSES
} from '@/views/onboarding_system_views/onboarding_ui_helpers';
import {
  build_case_form_sections,
  build_profile_rows,
  build_questionnaire_answers,
  build_stage_timeline,
  build_summary_rows,
  fetch_all_assigned_onboarding_cases,
  fetch_application_documents,
  fetch_queue_onboarding_cases,
  hydrate_case_detail,
  resolve_workflow_display_stage
} from '@/services/onboarding_journey_service';
import {
  verify_additional_application_document,
  verify_application_document
} from '@/services/onboarding_journey_application_service';
import {
  WORKFLOW_ACTION_STATE,
  workflowActionComputed,
  workflowActionMethods
} from '@/services/onboarding_workflow_action_service';
import { extract_saved_answers } from '@/services/onboarding_form_helpers';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import { resolve_onboarding_journey_filter } from '@/services/onboarding_journey_filter_service';

const first_value = (values, fallback = '') => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  return value === undefined ? fallback : value;
};
const DISALLOWED_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const to_safe_object_key = (value) => {
  const normalized = String(value || '').trim();
  return normalized && !DISALLOWED_OBJECT_KEYS.has(normalized) ? normalized : '';
};
const read_safe_object_value = (record, key, fallback = null) => {
  const safe_key = to_safe_object_key(key);
  if (!safe_key || !record || typeof record !== 'object') return fallback;
  return Object.prototype.hasOwnProperty.call(record, safe_key)
    ? Reflect.get(record, safe_key)
    : fallback;
};
const write_safe_object_value = (record, key, value) => {
  const safe_key = to_safe_object_key(key);
  if (!safe_key || !record || typeof record !== 'object') return;
  Reflect.set(record, safe_key, value);
};

function dispatch_system_loader(message) {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: message
  }));
}

function clear_system_loader_after_render(instance) {
  instance.$nextTick(() => {
    window.dispatchEvent(new CustomEvent('system-loader:hide'));
  });
}

const DEFAULT_DETAIL_TABS = [
  { id: 'summary', label: 'Summary', icon: 'bi-grid', badge: 'Current' }
];
const DOCUMENT_QUESTION_WORDS = ['document', 'upload', 'attachment', 'file', 'certificate', 'id copy', 'proof', 'bank statement'];
const MISSING_DOCUMENT_ANSWERS = ['not answered', 'not uploaded', 'pending'];

const DOCUMENT_STATUS_RULES = [
  {
    words: ['missing', 'expired', 'rejected'],
    tone: 'danger',
    action: 'Request reupload'
  },
  {
    words: ['pending', 'submitted', 'await', 'required'],
    tone: 'warning',
    action: 'Review'
  }
];
// const DOCUMENT_QUESTION_TYPES = ['file', 'document', 'document upload', 'file upload'];

function first_company_value(values, fallback = 'Unassigned company') {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== '');
  return value === undefined ? fallback : value;
}
function normalize_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function tab_matches_profile(tab = {}) {
  const label = normalize_text(`${tab?.id || ''} ${tab?.label || ''}`);
  return ['profile', 'personal', 'applicant'].some((word) => label.includes(word));
}

function pick_reference_tab(tabs = []) {
  return tabs.find(tab_matches_profile) || tabs[0] || null;
}
function normalize_document_label(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]{2,5}$/i, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
function prefer_document_candidate(current_document = {}, next_document = {}) {
  if (!current_document || typeof current_document !== 'object') return next_document;
  if (!next_document || typeof next_document !== 'object') return current_document;

  const current_score = [
    current_document?.source !== 'form_answer',
    has_uploaded_document_content(current_document),
    Boolean(String(current_document?.file_url || current_document?.presigned_url || '').trim())
  ].filter(Boolean).length;
  const next_score = [
    next_document?.source !== 'form_answer',
    has_uploaded_document_content(next_document),
    Boolean(String(next_document?.file_url || next_document?.presigned_url || '').trim())
  ].filter(Boolean).length;

  return next_score > current_score ? next_document : current_document;
}
function build_document_identity_key(document = {}) {
  const normalized_question_id = normalize_text(document?.question_id || document?.section_question_id || '');
  const normalized_file_url = normalize_text(
    document?.file_url || document?.presigned_url || document?.url || document?.document_url || ''
  );
  const normalized_label = normalize_document_label(
    document?.document || document?.question || document?.name || document?.document_name || document?.file_name || ''
  );
  const normalized_file_name = normalize_document_label(
    document?.file_name || document?.name || document?.document_name || ''
  );
  const normalized_name_key = [normalized_label, normalized_file_name]
    .filter(Boolean)
    .join('|');

  return normalized_name_key
    || normalized_label
    || normalized_file_name
    || normalized_file_url
    || normalized_question_id;
}
function matches_active_journey(case_item = {}, journey_filter = {}) {
  const normalized_filter_id = normalize_text(
    typeof journey_filter === 'string' ? journey_filter : journey_filter?.id
  );
  const normalized_filter_label = normalize_text(
    typeof journey_filter === 'object' ? journey_filter?.label : ''
  );
  const match_values = [normalized_filter_id, normalized_filter_label].filter(Boolean);
  if (!match_values.length) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ];
  return candidate_values.some((value) => match_values.includes(normalize_text(value)));
}
function get_status_candidates(case_item = {}) {
  const workflow_statuses = Array.isArray(case_item?.workflow_statuses) ? case_item.workflow_statuses : [];
  return [
    case_item?.stage,
    case_item?.detail,
    case_item?.current_workflow_status,
    case_item?.activation,
    case_item?.workflow_display_stage,
    ...workflow_statuses.map((status) => status?.status_name || status?.workflow_status_name || status?.name || status?.status)
  ].filter(Boolean);
}
function matches_gate_status(case_item = {}, target_status = '') {
  const normalized_target = normalize_text(target_status);
  if (!normalized_target) return false;
  return get_status_candidates(case_item).some((value) => normalize_text(value).includes(normalized_target));
}
const normalize_company_label = (value, fallback = 'Unassigned company') => {
  const label = String(value || '').trim();
  if (!label) return fallback;

  const normalized = label.toLowerCase();
  if (['employee', 'supplier', 'client', 'intern', 'contractor', 'user'].includes(normalized)) {
    return fallback;
  }

  return label;
};
function with_workflow_stage(case_item) {
  const workflow_stage = resolve_workflow_display_stage(case_item);
  return workflow_stage
    ? {
        ...case_item,
        current_workflow_status: workflow_stage,
        stage: workflow_stage,
        detail: workflow_stage
      }
    : case_item;
}
function build_initials(value = '', fallback = 'U') {
  const parts = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return fallback;
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
function clamp_progress(value) {
  const numeric_value = Number(value);
  if (!Number.isFinite(numeric_value)) return 0;
  return Math.max(0, Math.min(100, Math.round(numeric_value)));
}
function resolve_case_progress(case_item = {}, document_count = 0, open_document_count = 0) {
  const explicit_progress = first_value([
    case_item?.progress,
    case_item?.completion_percentage,
    case_item?.completion_percent,
    case_item?.progress_percentage
  ], Number.NaN);
  const normalized_progress = clamp_progress(explicit_progress);
  if (normalized_progress > 0) return normalized_progress;
  if (!document_count) return 0;
  return clamp_progress(((document_count - open_document_count) / document_count) * 100);
}
function resolve_case_progress_tone(progress = 0) {
  if (progress >= 100) return 'success';
  if (progress >= 60) return 'navy';
  return 'warning';
}

function has_uploaded_document_content(document = {}) {
  return Boolean(first_value([
    document?.file_name,
    document?.name,
    document?.document_name,
    document?.answer?.file_name,
    document?.answer?.presigned_url,
    document?.answer?.file_url,
    document?.presigned_url,
    document?.file_url,
    document?.url,
    document?.document_url
  ], ''));
}
function normalize_form_answer_document(answer = {}, form = {}, index = 0) {
  return {
    id: answer?.section_question_id || `${form?.application_form_id || form?.form_id || form?.id || 'form'}-answer-${index}`,
    question: first_value([
      answer?.question_label,
      answer?.file_name,
      form?.form_name,
      form?.form?.form_name
    ], 'Uploaded document'),
    file_name: first_value([
      answer?.file_name,
      answer?.answer_text
    ], ''),
    file_url: String(answer?.file_url || '').trim(),
    presigned_url: String(answer?.file_url || '').trim(),
    section: first_value([
      form?.form_name,
      form?.form?.form_name
    ], 'Onboarding form'),
    section_question_id: answer?.section_question_id || null,
    question_id: answer?.section_question_id || null,
    is_verified: false,
    application_form_id: form?.application_form_id || form?.id || '',
    source: 'form_answer'
  };
}
function build_form_answer_documents(case_item = {}) {
  const detailed_forms = Array.isArray(case_item?.detailed_forms) ? case_item.detailed_forms : [];

  return detailed_forms.flatMap((form) => (
    extract_saved_answers(form?.application_form)
      .filter((answer) => Boolean(String(answer?.file_url || '').trim() || String(answer?.file_name || '').trim()))
      .map((answer, index) => normalize_form_answer_document(answer, form, index))
  ));
}
function build_case_source_documents(case_item = {}) {
  const explicit_documents = Array.isArray(case_item?.documents)
    ? case_item.documents.filter((document) => has_uploaded_document_content(document))
    : [];
  const form_documents = build_form_answer_documents(case_item);
  const deduped_documents = new Map();

  [...explicit_documents, ...form_documents].forEach((document) => {
    const normalized_file_url = normalize_text(document?.file_url || document?.presigned_url || '');
    const normalized_file_name = normalize_text(document?.file_name || document?.name || document?.document_name || '');
    const normalized_question = normalize_text(document?.question || '');
    const normalized_label = normalize_document_label(
      document?.question || document?.name || document?.document_name || document?.file_name || ''
    );
    const normalized_question_id = normalize_text(document?.question_id || document?.section_question_id || '');
    const document_key = normalized_file_url
      || normalized_question_id
      || normalized_label
      || [normalized_file_name, normalized_question].filter(Boolean).join('|')
      || normalized_question;

    deduped_documents.set(
      document_key,
      prefer_document_candidate(deduped_documents.get(document_key), document)
    );
  });

  return Array.from(deduped_documents.values());
}

function build_document_company_source(case_item) {
  return {
    company_name: first_company_value([
      case_item?.company_name,
      case_item?.organisation_name,
      case_item?.organization_name,
      case_item?.profile?.company_name,
      case_item?.profile?.organisation_name,
      case_item?.profile?.organization_name,
      case_item?.profile?.company?.name,
      case_item?.profile?.organisation?.name,
      case_item?.profile?.organization?.name,
      case_item?.onboarding_project_name,
      case_item?.profile?.project?.name,
      case_item?.profile?.project_name,
      case_item?.project?.name,
      case_item?.project_name
    ]),
    case_item
  };
}

export default {
  name: 'OnboardingManageDocuments',
  components: {
    EntityNameEditButton
  },
  data: () => ({
    active_filter: 'all',
    is_loading_documents: false,
    documents_error: '',
    selected_document: null,
    is_detail_fullscreen: false,
    is_loading_detail: false,
    preview_modal_visible: false,
    preview_modal_title: '',
    preview_modal_url: '',
    preview_modal_type: '',
    selected_document_upload_file: null,
    selected_document_upload_preview_url: '',
    selected_document_upload_error: '',
    selected_document_upload_success: '',
    is_uploading_selected_document: false,
    document_review_comments: Object.create(null),
    document_review_in_progress_by_id: Object.create(null),
    document_review_feedback: '',
    document_review_feedback_tone: 'success',
    ...WORKFLOW_ACTION_STATE,
    detail_tab_overflow: {
      left: false,
      right: false
    },
    active_detail_tab: 'summary',
    detail_tabs: DEFAULT_DETAIL_TABS,
    case_form_sections: [],
    summary_rows: [],
    profile_rows: [],
    stage_timeline: [],
    questionnaire_answers: [],
    filters: [
      { label: 'All', count: '0', tone: 'all' },
      { label: 'Open', tone: 'open' },
      { label: 'Mine', tone: 'mine' },
      { label: 'Blocked', tone: 'blocked' }
    ],
    documents: [],
    document_rule_assignments_by_form: Object.create(null),
    document_rule_requests_by_form: Object.create(null),
    ...tablePaginationState()
  }),
  computed: {
    filtered_documents() {
      if (this.active_filter === 'open') {
        return this.filter_table_rows(this.documents.filter((item) => item.status_tone !== 'success'));
      }

      if (this.active_filter === 'mine') {
        return this.filter_table_rows(this.documents.filter((item) => this.is_my_document_case(item)));
      }

      if (this.active_filter === 'blocked') {
        return this.filter_table_rows(
          this.documents.filter((item) => ['danger', 'warning'].includes(item.status_tone))
        );
      }

      return this.filter_table_rows(this.documents);
    },
    table_total_rows() {
      return this.filtered_documents.length;
    },
    paginated_documents() {
      return this.paginate_table_rows(this.filtered_documents);
    },
    ...tablePaginationComputed,
    active_journey_filter() {
      return resolve_onboarding_journey_filter(this.$route);
    },
    selected_case_documents() {
      return this.selected_document?.case_documents?.length
        ? this.selected_document.case_documents
        : this.selected_document ? [this.selected_document] : [];
    },
    active_form_section() {
      const forms = Array.isArray(this.case_form_sections) ? this.case_form_sections : [];
      for (const form of forms) {
        const matched_section = (Array.isArray(form?.sections) ? form.sections : [])
          .find((section) => this.tab_id_for_section(form, section) === this.active_detail_tab);
        if (matched_section) return matched_section;
      }

      return null;
    },
    active_form_section_is_document_section() {
      const section = this.active_form_section;
      const questions = Array.isArray(section?.sub_sections)
        ? section.sub_sections.flatMap((sub_section) => Array.isArray(sub_section?.questions) ? sub_section.questions : [])
        : [];
      const section_label = normalize_text(section?.label || '');

      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => section_label.includes(word))
        || questions.some((question) => this.is_document_question(question));
    },
    ...workflowActionComputed
  },
  mounted() {
    this.load_documents();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    window.addEventListener('resize', this.update_detail_tab_overflow);
  },
  activated() {
    if (!this.is_loading_documents) {
      clear_system_loader_after_render(this);
    }
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    window.removeEventListener('resize', this.update_detail_tab_overflow);
  },
  watch: {
    active_journey_filter() {
      this.close_document_detail();
      this.load_documents();
    }
  },
  methods: {
    ...workflowActionMethods,
    ...tablePaginationMethods,
    is_exact_entity_role() {
      return String(this.$store.getters.currentRole || '').trim().toLowerCase() === 'entity';
    },
    async fetch_document_cases() {
      if (this.is_exact_entity_role()) {
        return fetch_all_assigned_onboarding_cases({ _: Date.now() });
      }

      return fetch_queue_onboarding_cases({ _: Date.now() });
    },
    open_document_case_page(document_item = {}, event) {
      event?.stopPropagation?.();

      const case_item = document_item?.case_item;
      const application_id = Number(case_item?.application_id || case_item?.id || 0);
      if (!application_id) return;

      dispatch_system_loader('Opening onboarding form...');
      this.$router.push({
        name: 'MyQueueForm',
        params: {
          applicationId: String(application_id)
        },
        query: {
          name: case_item?.name || document_item?.case_name || '',
          company: case_item?.company_name || document_item?.company_name || '',
          entity_type_id: String(case_item?.entity_type_id || case_item?.threshold_entity_type_id_used || ''),
          journey: case_item?.journey || document_item?.type || '',
          status: 'Documents',
          application_form_id: '',
          source: 'documents-gate'
        }
      });
    },
    handle_onboarding_data_updated() {
      this.reset_inline_upload_document();
      this.load_documents();
    },
    reset_document_review_state() {
      this.document_review_comments = Object.create(null);
      this.document_review_in_progress_by_id = Object.create(null);
      this.document_review_feedback = '';
      this.document_review_feedback_tone = 'success';
    },
    reset_selected_document_upload_state() {
      if (this.selected_document_upload_preview_url) {
        URL.revokeObjectURL(this.selected_document_upload_preview_url);
      }
      this.selected_document_upload_file = null;
      this.selected_document_upload_preview_url = '';
      this.selected_document_upload_error = '';
      this.selected_document_upload_success = '';
      this.is_uploading_selected_document = false;
    },
    reset_inline_upload_document() {
      this.reset_selected_document_upload_state();
      this.inline_upload_document = null;
    },
    selected_document_match_key(document_item = {}) {
      return [
        document_item?.case_item?.application_id || '',
        document_item?.form_id || '',
        document_item?.section_question_id || ''
      ].join(':');
    },
    is_upload_row_open(document_item = {}) {
      if (!this.inline_upload_document) return false;
      return this.selected_document_match_key(this.inline_upload_document) === this.selected_document_match_key(document_item);
    },
    is_my_document_case(document_case = {}) {
      if (this.is_exact_entity_role()) return true;

      const current_user_id = Number(this.$store?.getters?.currentUserId || 0);
      if (!current_user_id) return false;

      const candidate_ids = [
        document_case?.case_item?.owner_id,
        document_case?.case_item?.assigned_user_id,
        document_case?.case_item?.assigned_admin_id,
        document_case?.case_item?.assigned_administrator?.id,
        document_case?.case_item?.owner?.id
      ]
        .map((value) => Number(value || 0))
        .filter((value) => value > 0);

      return candidate_ids.includes(current_user_id);
    },
    get_workflow_action_case() {
      return this.selected_document?.case_item || null;
    },
    async on_workflow_action_success() {
      this.close_document_detail();
      await this.load_documents();
    },
    document_review_comment(document_item = {}) {
      return read_safe_object_value(this.document_review_comments, document_item?.id, '');
    },
    set_document_review_comment(document_item = {}, value = '') {
      if (!document_item?.id) return;
      write_safe_object_value(this.document_review_comments, document_item.id, String(value || ''));
    },
    is_document_review_supported(document_item = {}) {
      const source = normalize_text(document_item?.source);
      if (source === 'application') {
        return Number(document_item?.application_form_id || 0) > 0
          && Number(document_item?.section_id || 0) > 0
          && Number(document_item?.question_id || 0) > 0;
      }

      if (source === 'additional') {
        return Number(document_item?.id || 0) > 0;
      }

      return false;
    },
    unsupported_document_review_reason(document_item = {}) {
      const source = normalize_text(document_item?.source);
      if (!source) return 'This document source cannot be reviewed from this screen yet.';
      if (source === 'application' || source === 'additional') return '';
      if (source === 'submission') return 'Signed documents are informational here and are not manually approved from this screen.';
      if (source === 'journal') return 'Journal documents are informational here and are not manually approved from this screen.';
      if (source === 'kyc_report') return 'KYC reports are generated outputs and are not manually approved from this screen.';
      return 'This document source cannot be reviewed from this screen yet.';
    },
    is_document_review_busy(document_item = {}) {
      return Boolean(read_safe_object_value(this.document_review_in_progress_by_id, document_item?.id, false));
    },
    is_document_review_approved(document_item = {}) {
      const verification_state = normalize_text(document_item?.verification_state);
      return document_item?.is_verified === true || verification_state === 'verified';
    },
    document_review_status_label(document_item = {}) {
      const verification_state = normalize_text(document_item?.verification_state);
      if (this.is_document_review_approved(document_item)) return 'Approved';
      if (verification_state === 'rejected') return 'Rejected';
      if (this.has_document_preview(document_item)) return 'Pending review';
      return 'Awaiting upload';
    },
    async run_document_review(document_item = {}, is_verified = true) {
      if (!this.is_document_review_supported(document_item)) {
        this.document_review_feedback = this.unsupported_document_review_reason(document_item);
        this.document_review_feedback_tone = 'warning';
        return;
      }

      const document_id = document_item?.id;
      const application_id = Number(
        document_item?.case_item?.application_id
        || document_item?.case_item?.id
        || this.selected_document?.case_item?.application_id
        || this.selected_document?.case_item?.id
        || 0
      );
      const selected_document_key = build_document_identity_key(document_item);
      const verification_comment = this.document_review_comment(document_item);

      write_safe_object_value(this.document_review_in_progress_by_id, document_id, true);
      this.document_review_feedback = '';

      try {
        if (normalize_text(document_item?.source) === 'additional') {
          await verify_additional_application_document({
            application_id,
            additional_document_id: document_item.id,
            is_verified,
            verification_comment
          });
        } else {
          await verify_application_document({
            application_id,
            application_form_id: document_item.application_form_id,
            section_id: document_item.section_id,
            question_id: document_item.question_id,
            is_verified,
            verification_comment
          });
        }

        this.document_review_feedback = is_verified
          ? 'Document approved successfully.'
          : 'Document rejected successfully.';
        this.document_review_feedback_tone = is_verified ? 'success' : 'warning';
        await this.reload_document_review_state(application_id, selected_document_key);
      } catch (error) {
        this.document_review_feedback = error?.response?.data?.message || error?.message || 'Unable to update the document review decision.';
        this.document_review_feedback_tone = 'danger';
      } finally {
        write_safe_object_value(this.document_review_in_progress_by_id, document_id, false);
      }
    },
    async reload_document_review_state(application_id = 0, selected_document_key = '') {
      await this.load_documents();

      if (!application_id || !this.selected_document) return;

      const refreshed_case = this.documents.find((item) => Number(item?.case_item?.application_id || item?.id || 0) === Number(application_id));
      if (!refreshed_case) return;

      const refreshed_document = (Array.isArray(refreshed_case.case_documents) ? refreshed_case.case_documents : [])
        .find((candidate) => build_document_identity_key(candidate) === selected_document_key)
        || refreshed_case;

      this.selected_document = refreshed_document;
      this.profile_rows = refreshed_document.profile_rows || [];
      this.stage_timeline = refreshed_document.stage_timeline || [];
      this.questionnaire_answers = refreshed_document.questionnaire_answers || [];
      this.case_form_sections = build_case_form_sections(refreshed_document.case_item || {});
      this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
    },
    tab_id_for_section(form, section) {
      return `form-${form?.id || 'section'}-${section?.id || section?.label || 'item'}`;
    },
    build_detail_tabs(form_sections = []) {
      const section_tabs = [];
      form_sections.forEach((form) => {
        (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
          section_tabs.push({
            id: this.tab_id_for_section(form, section),
            label: section?.label || form?.form_name || 'Section',
            icon: this.tab_icon_for_section(form, section)
          });
        });
      });
      const reference_tab = pick_reference_tab(section_tabs);
      const tabs = [{
        id: 'summary',
        label: 'Summary',
        icon: 'bi-grid',
        badge: 'Current'
      }, {
        id: 'agreements',
        label: 'Uploaded Documents',
        icon: 'bi-folder2-open',
        count: this.selected_case_documents.length ? String(this.selected_case_documents.length) : ''
      }];
      if (reference_tab) {
        tabs.push({
          ...reference_tab,
          label: 'Application Reference',
          badge: 'Reference'
        });
      }
      section_tabs
        .filter((tab) => tab.id !== reference_tab?.id)
        .forEach((tab) => {
          tabs.push(tab);
        });
      return tabs;
    },
    tab_icon_for_section(form = {}, section = {}) {
      const label = normalize_text(`${form?.form_name || ''} ${section?.label || ''}`);
      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => label.includes(word))
        ? 'bi-folder'
        : 'bi-list-check';
    },
    format_detail_date(value) {
      if (!value) return 'Not submitted';
      const parsed_date = new Date(value);
      if (Number.isNaN(parsed_date.getTime())) return String(value);
      return parsed_date.toLocaleDateString('en-ZA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },
    detail_header_eyebrow() {
      const journey = first_value([
        this.selected_document?.type,
        this.selected_document?.case_item?.journey
      ], 'Onboarding');
      const stage = first_value([
        this.selected_document?.case_item?.stage,
        this.selected_document?.case_item?.gate,
        'Documents gate'
      ], 'Documents gate');
      return `${journey} onboarding · ${stage}`;
    },
    detail_header_subtitle() {
      const owner = first_value([
        this.selected_document?.reviewer,
        this.selected_document?.case_item?.owner
      ], 'Unassigned');
      const risk = first_value([
        resolve_case_risk_label(this.selected_document?.case_item),
        this.selected_document?.case_item?.risk_label
      ], 'Unknown risk');
      const pipeline_age = first_value([
        resolve_case_sla_label(this.selected_document?.case_item),
        this.selected_document?.case_item?.due,
        this.selected_document?.case_item?.pipeline_age,
        this.selected_document?.case_item?.time_in_pipeline,
        this.selected_document?.case_item?.age_label
      ], '');

      return [owner, risk, pipeline_age].filter(Boolean).join(' · ');
    },
    detail_activation_label() {
      return first_value([
        this.selected_document?.case_item?.activation,
        resolve_case_sla_label(this.selected_document?.case_item),
        this.selected_document?.status_tone === 'success' ? 'Ready' : '',
        this.selected_document?.status,
        'Blocked'
      ], 'Blocked');
    },
    get_document_company_name(document_item) {
      return normalize_company_label(first_company_value([
        document_item?.company_name,
        document_item?.case_item?.company_name,
        document_item?.case_item?.organisation_name,
        document_item?.case_item?.organization_name,
        document_item?.case_item?.profile?.company_name,
        document_item?.case_item?.profile?.organisation_name,
        document_item?.case_item?.profile?.organization_name,
        document_item?.case_item?.profile?.company?.name,
        document_item?.case_item?.profile?.organisation?.name,
        document_item?.case_item?.profile?.organization?.name,
        document_item?.case_item?.onboarding_project_name,
        document_item?.case_item?.profile?.project?.name,
        document_item?.case_item?.profile?.project_name,
        document_item?.case_item?.project?.name,
        document_item?.case_item?.project_name
      ]));
    },
    get_document_status(document) {
      if (document?.is_verified === true) return { status: 'Verified', tone: 'success', action: '-' };

      if (normalize_text(document?.verification_state) === 'rejected') {
        return { status: 'Rejected', tone: 'danger', action: 'Request reupload' };
      }

      if (normalize_text(document?.verification_state) === 'pending' && (document?.file_name || document?.file_url)) {
        return { status: 'Pending review', tone: 'warning', action: 'Review' };
      }

      if (!document?.file_name && !document?.file_url) {
        const missingStatus = document?.is_mandatory ? 'Missing' : 'Not submitted';
        return {
          status: missingStatus,
          tone: document?.is_mandatory ? 'danger' : 'secondary',
          action: document?.is_mandatory ? 'Request upload' : 'View form'
        };
      }

      const status = document?.status || document?.document_status || document?.verification_status || 'Submitted';
      const normalized_status = String(status).toLowerCase();
      const matching_rule = DOCUMENT_STATUS_RULES.find((rule) => (
        rule.words.some((word) => normalized_status.includes(word))
      ));

      return matching_rule
        ? { status, tone: matching_rule.tone, action: matching_rule.action }
        : { status, tone: 'secondary', action: 'Review' };
    },
    progress_bar_color(tone) {
      return get_mapped_value(PROGRESS_COLORS, tone, '#416f6e');
    },
    risk_badge_class(tone) {
      return get_mapped_value(RISK_BADGE_CLASSES, tone, 'bg-secondary-subtle text-secondary-emphasis');
    },
    activation_badge_class_for_case(tone) {
      return get_mapped_value(ACTIVATION_BADGE_CLASSES, tone, 'bg-secondary-subtle text-secondary-emphasis');
    },
    journey_dot_style(tone) {
      return get_journey_dot_style(tone);
    },
    resolve_document_workflow_names(case_item = {}, form_id = 0) {
      const workflow_bindings = get_onboarding_workflow_bindings(case_item);
      const matchingBindingNames = workflow_bindings
        .filter((binding) => {
          const bindingFormIds = extract_form_binding_ids(binding);
          return !form_id || !bindingFormIds.length || bindingFormIds.includes(Number(form_id));
        })
        .map((binding) => first_value([binding?.workflow_name, binding?.workflow?.name, binding?.name, binding?.title], ''))
        .filter(Boolean);

      if (matchingBindingNames.length) {
        return unique_values(matchingBindingNames);
      }

      const workflowStatusNames = (Array.isArray(case_item?.workflow_statuses) ? case_item.workflow_statuses : [])
        .map((status) => status?.workflow_name)
        .filter(Boolean);

      return unique_values(workflowStatusNames);
    },
    async fetch_form_compliance_assignments(form_id) {
      const normalizedFormId = Number(form_id || 0);
      if (normalizedFormId <= 0) return Object.create(null);

      const cacheKey = String(normalizedFormId);
      const cachedAssignments = read_safe_object_value(this.document_rule_assignments_by_form, cacheKey, null);
      if (cachedAssignments) return cachedAssignments;

      const cachedRequest = read_safe_object_value(this.document_rule_requests_by_form, cacheKey, null);
      if (cachedRequest) return cachedRequest;

      const request = (async () => {
        const assignments = Object.create(null);
        const rulesResponse = await axios.get(
          GET_FORM_COMPLIANCE_RULES_URL,
          get_request_config({ params: { form_id: normalizedFormId } })
        ).catch(() => null);

        const rules = extract_response_collection(rulesResponse?.data, ['rules', 'data', 'results']);
        await Promise.all(rules.map(async (rule) => {
          const complianceRuleId = Number(first_value([rule?.id, rule?.compliance_rule_id], 0));
          if (complianceRuleId <= 0) return;

          const ruleName = first_value([rule?.name, rule?.rule_name], `Rule ${complianceRuleId}`);
          const assignmentResponse = await axios.get(
            GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
            get_request_config({ params: { compliance_rule_id: complianceRuleId } })
          ).catch(() => null);

          const assignedForms = extract_response_collection(assignmentResponse?.data, ['data', 'results']);
          assignedForms.forEach((assignedForm) => {
            const assignedFormId = Number(first_value([assignedForm?.form_id, assignedForm?.id], 0));
            if (assignedFormId > 0 && assignedFormId !== normalizedFormId) return;

            const mainSections = Array.isArray(assignedForm?.main_sections) ? assignedForm.main_sections : [];
            mainSections.forEach((mainSection) => {
              const subSections = Array.isArray(mainSection?.sub_sections) ? mainSection.sub_sections : [];
              subSections.forEach((subSection) => {
                const questions = Array.isArray(subSection?.questions) ? subSection.questions : [];
                questions
                  .filter((question) => is_document_question(question))
                  .forEach((question) => {
                    const sectionQuestionId = Number(first_value([question?.section_question_id, question?.id], 0));
                    if (sectionQuestionId <= 0) return;

                    const existing = Array.isArray(assignments[sectionQuestionId]) ? assignments[sectionQuestionId] : [];
                    assignments[sectionQuestionId] = unique_values([
                      ...existing,
                      ruleName
                    ]);
                  });
              });
            });
          });
        }));

        this.document_rule_assignments_by_form[cacheKey] = assignments;
        delete this.document_rule_requests_by_form[cacheKey];
        return assignments;
      })().catch((error) => {
        delete this.document_rule_requests_by_form[cacheKey];
        throw error;
      });

      this.document_rule_requests_by_form[cacheKey] = request;
      return request;
    },
    find_document_answer(question = {}, savedAnswers = { byId: new Map(), byLabel: new Map() }, uploadedDocuments = { byId: new Map(), byLabel: new Map() }) {
      const sectionQuestionId = Number(first_value([question?.section_question_id, question?.id], 0));
      const questionKey = get_question_lookup_key(question);
      const savedAnswer = savedAnswers.byId.get(sectionQuestionId) || savedAnswers.byLabel.get(questionKey) || null;
      const uploadedDocument = uploadedDocuments.byId.get(sectionQuestionId) || uploadedDocuments.byLabel.get(questionKey) || null;
      return { savedAnswer, uploadedDocument };
    },
    async build_case_document_forms(case_item) {
      const detailedForms = Array.isArray(case_item?.detailed_forms) ? case_item.detailed_forms : [];
      const configuredForms = get_onboarding_form_bindings(case_item)
        .map((binding, index) => normalize_bound_form_candidate(binding, index))
        .filter((form) => form.form_id > 0);

      const formsById = new Map();

      detailedForms.forEach((form) => {
        const formId = get_form_definition_id(form);
        if (formId > 0) {
          formsById.set(formId, form);
        }
      });

      await Promise.all(configuredForms.map(async (form) => {
        if (formsById.has(form.form_id)) return;

        const structure = await fetch_form_structure(form.form_id).catch(() => []);
        formsById.set(form.form_id, {
          ...form,
          structure: Array.isArray(structure) ? structure : []
        });
      }));

      return Array.from(formsById.values());
    },
    build_document_items(case_item) {
      const case_with_stage = with_workflow_stage(case_item);
      const source_documents = build_case_source_documents(case_with_stage);
      const resolved_company_name = this.get_document_company_name(build_document_company_source(case_with_stage));
      const raw_case_documents = source_documents.map((document, index) => {
        const status = this.get_document_status(document);
        return {
          ...document,
          id: document?.id || `${case_with_stage.application_id || case_with_stage.entity_profile_id || case_with_stage.id || 'document'}-${document?.question || document?.file_name || document?.name || index}`,
          document: document?.question || document?.file_name || document?.name || 'Document',
          file_name: first_value([
            document?.file_name,
            document?.name,
            document?.document_name,
            document?.answer?.file_name
          ], ''),
          file_type: first_value([
            document?.content_type,
            document?.mime_type,
            document?.file_type,
            document?.document_type,
            document?.answer?.content_type,
            document?.answer?.mime_type
          ], ''),
          company_name: resolved_company_name,
          case_name: first_value([
            case_with_stage.name,
            case_with_stage.display_name,
            case_with_stage.profile?.name,
            case_with_stage.profile?.full_name,
            case_with_stage.profile?.entity_name,
            case_with_stage.entity_name
          ], 'Unassigned entity'),
          type: case_with_stage.journey,
          file_url: first_value([
            document?.presigned_url,
            document?.file_url,
            document?.url,
            document?.document_url,
            document?.answer?.presigned_url,
            document?.answer?.file_url
          ], ''),
          status: status.status,
          status_tone: status.tone,
          reviewer: case_with_stage.owner || 'Unassigned',
          case_item: case_with_stage,
          case_documents: [],
          stage_timeline: build_stage_timeline(case_with_stage),
          questionnaire_answers: build_questionnaire_answers(case_with_stage),
          profile_rows: build_profile_rows(case_with_stage)
        };
      });
      const deduped_case_documents = new Map();

      raw_case_documents.forEach((document_item) => {
        const document_key = build_document_identity_key(document_item)
          || normalize_text(document_item.id || '');
        const preferred_document = prefer_document_candidate(
          deduped_case_documents.get(document_key),
          document_item
        );

        deduped_case_documents.set(document_key, preferred_document);
      });

      const case_documents = Array.from(deduped_case_documents.values());

      return case_documents.map((document_item, index) => ({
        ...document_item,
        case_documents,
        id: document_item.id || `${case_with_stage.application_id || case_with_stage.entity_profile_id || case_with_stage.id || 'document'}-document-${index}`
      }));
    },
    build_document_case_item(case_item = {}) {
      const case_with_stage = with_workflow_stage(case_item);
      const case_documents = this.build_document_items(case_with_stage);
      const resolved_company_name = this.get_document_company_name(build_document_company_source(case_with_stage));
      const open_document_count = case_documents.filter((item) => item.status_tone !== 'success').length;
      const blocked_document_count = case_documents.filter((item) => ['danger', 'warning'].includes(item.status_tone)).length;
      const document_count = case_documents.length;
      const progress = resolve_case_progress(case_with_stage, document_count, open_document_count);
      const progress_tone = resolve_case_progress_tone(progress);
      const owner = first_value([
        case_with_stage.owner,
        case_with_stage.assigned_administrator?.full_name,
        case_with_stage.assigned_administrator?.name
      ], 'Unassigned');
      const activation = first_value([
        case_with_stage.activation,
        resolve_case_sla_label(case_with_stage),
        open_document_count ? 'Pending' : 'Ready'
      ], 'Pending');
      const activation_tone = open_document_count
        ? (blocked_document_count ? 'blocked' : resolve_case_sla_tone(case_with_stage))
        : 'ready';

      return {
        id: case_with_stage.application_id || case_with_stage.entity_profile_id || case_with_stage.id || `${resolved_company_name}-${document_count}`,
        case_name: first_value([
          case_with_stage.name,
          case_with_stage.display_name,
          case_with_stage.profile?.name,
          case_with_stage.profile?.full_name,
          case_with_stage.profile?.entity_name,
          case_with_stage.entity_name
        ], 'Unassigned entity'),
        case_subtitle: first_value([
          case_with_stage.detail,
          case_with_stage.current_workflow_status,
          case_with_stage.stage
        ], 'Documents review'),
        company_name: resolved_company_name,
        type: first_value([case_with_stage.journey], 'Onboarding'),
        journey_tone: normalize_text(first_value([
          case_with_stage.journey_tone,
          case_with_stage.journey_key,
          case_with_stage.entity_type_id
        ], '')),
        progress,
        progress_tone,
        risk: resolve_case_risk_label(case_with_stage),
        risk_tone: resolve_case_risk_tone(case_with_stage),
        stage: first_value([
          case_with_stage.stage,
          case_with_stage.current_workflow_status,
          case_with_stage.gate
        ], 'Documents gate'),
        reviewer: owner,
        owner_initials: build_initials(owner),
        eta: first_value([
          case_with_stage.eta,
          case_with_stage.onboarding_activate_by,
          case_with_stage.sla,
          '–'
        ], '–'),
        activation,
        activation_tone,
        status: open_document_count ? `${open_document_count} open` : 'Approved',
        status_tone: open_document_count ? (blocked_document_count ? 'danger' : 'warning') : 'success',
        open_document_count,
        blocked_document_count,
        document_count,
        case_documents,
        case_item: case_with_stage,
        stage_timeline: build_stage_timeline(case_with_stage),
        questionnaire_answers: build_questionnaire_answers(case_with_stage),
        profile_rows: build_profile_rows(case_with_stage)
      };
    },
    update_document_filters() {
      this.filters = this.filters.map((filter) => {
        if (filter.tone === 'all') {
          return { ...filter, count: String(this.documents.length) };
        }
        if (filter.tone === 'open') {
          return { ...filter, count: String(this.documents.filter((item) => item.status_tone !== 'success').length) };
        }
        if (filter.tone === 'mine') {
          return { ...filter, count: String(this.documents.filter((item) => this.is_my_document_case(item)).length) };
        }
        if (filter.tone === 'blocked') {
          return { ...filter, count: String(this.documents.filter((item) => ['danger', 'warning'].includes(item.status_tone)).length) };
        }
        return filter;
      });
    },
    async load_documents() {
      this.is_loading_documents = true;
      this.documents_error = '';
      dispatch_system_loader('Loading documents...');

      try {
        const cases = await this.fetch_document_cases();
        const scoped_cases = cases
          .filter((case_item) => matches_active_journey(case_item, this.active_journey_filter))
          .filter(Boolean);
        const cases_with_documents = await Promise.all(
          scoped_cases.map((case_item) => (
            fetch_application_documents(case_item?.application_id)
              .then((documents) => ({
                ...case_item,
                documents: Array.isArray(documents) ? documents : []
              }))
              .catch(() => ({
                ...case_item,
                documents: Array.isArray(case_item?.documents) ? case_item.documents : []
              }))
          ))
        );
        this.documents = cases_with_documents
          .map((case_item) => this.build_document_case_item(case_item))
          .filter((item) => item.document_count > 0 || matches_gate_status(item.case_item, 'Documents'));
        this.update_document_filters();
      } catch (error) {
        this.documents = [];
        this.documents_error = error?.response?.data?.message || error?.message || 'Unable to load documents.';
      } finally {
        this.is_loading_documents = false;
        clear_system_loader_after_render(this);
      }
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    open_document_detail(document_item) {
      dispatch_system_loader('Loading document review...');
      this.reset_workflow_action_state();
      this.reset_selected_document_upload_state();
      this.reset_document_review_state();
      this.selected_document = document_item;
      this.is_loading_detail = true;
      this.summary_rows = build_summary_rows(document_item.case_item || {});
      this.profile_rows = document_item.profile_rows || [];
      this.stage_timeline = document_item.stage_timeline || [];
      this.questionnaire_answers = document_item.questionnaire_answers || [];
      this.case_form_sections = build_case_form_sections(document_item.case_item || {});
      this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
      this.active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;
      this.$nextTick(() => this.update_detail_tab_overflow());

      if (!document_item?.case_item) {
        this.is_loading_detail = false;
        clear_system_loader_after_render(this);
        return;
      }

      hydrate_case_detail(document_item.case_item)
        .then((hydrated_case) => {
          const next_case_item = { ...document_item.case_item, ...hydrated_case };
          this.selected_document = {
            ...this.selected_document,
            case_item: next_case_item
          };
          this.summary_rows = build_summary_rows(next_case_item);
          this.profile_rows = build_profile_rows(next_case_item);
          this.stage_timeline = build_stage_timeline(next_case_item);
          this.questionnaire_answers = build_questionnaire_answers(next_case_item);
          this.case_form_sections = build_case_form_sections(next_case_item);
          this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
          this.$nextTick(() => this.update_detail_tab_overflow());
          this.load_workflow_actions_for_selected_document();
        })
        .catch(() => {
          this.selected_document = document_item;
          this.summary_rows = build_summary_rows(document_item.case_item || {});
          this.load_workflow_actions_for_selected_document();
        })
        .finally(() => {
          this.is_loading_detail = false;
          clear_system_loader_after_render(this);
        });
    },
    close_document_detail() {
      this.reset_selected_document_upload_state();
      this.reset_document_review_state();
      this.selected_document = null;
      this.is_detail_fullscreen = false;
      this.is_loading_detail = false;
      this.case_form_sections = [];
      this.summary_rows = [];
      this.detail_tabs = DEFAULT_DETAIL_TABS;
      this.detail_tab_overflow = { left: false, right: false };
      this.reset_workflow_action_state();
    },
    async load_workflow_actions_for_selected_document() {
      await this.load_workflow_actions_for_case(this.selected_document?.case_item);
    },
    document_file_hint(document_item) {
      return String(first_value([
        document_item?.file_name,
        document_item?.document,
        document_item?.file_url
      ], '')).toLowerCase();
    },
    is_image_file(document_item) {
      const file_type = String(document_item?.file_type || '').toLowerCase();

      return file_type.startsWith('image/')
        || /\.(png|jpe?g|gif|webp|bmp|svg)($|\?)/i.test(this.document_file_hint(document_item));
    },
    is_pdf_file(document_item) {
      const file_type = String(document_item?.file_type || '').toLowerCase();

      return file_type === 'application/pdf'
        || /\.pdf($|\?)/i.test(this.document_file_hint(document_item));
    },
    open_document_preview(document_item) {
      const preview_url = document_item?.file_url || '';

      if (!preview_url) {
        this.open_document_detail(document_item);
        return;
      }

      this.preview_modal_title = document_item?.document || 'Uploaded document';
      this.preview_modal_url = preview_url;
      this.preview_modal_type = this.is_image_file(document_item)
        ? 'image'
        : this.is_pdf_file(document_item)
          ? 'pdf'
          : 'file';
      this.preview_modal_visible = true;
    },
    has_document_preview(document_item = {}) {
      return Boolean(String(document_item?.file_url || '').trim());
    },
    async open_document_form(document_item, event) {
      this.open_document_case_page(document_item, event);
    },
    preview_modal_src() {
      if (!this.preview_modal_url) {
        return '';
      }

      if (this.preview_modal_type === 'pdf') {
        return `${this.preview_modal_url}#toolbar=1&navpanes=0&view=FitH`;
      }

      return this.preview_modal_url;
    },
    close_preview_modal() {
      this.preview_modal_visible = false;
      this.preview_modal_title = '';
      this.preview_modal_url = '';
      this.preview_modal_type = '';
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
      this.$nextTick(() => {
        this.scroll_active_detail_tab_into_view();
        this.update_detail_tab_overflow();
      });
    },
    current_detail_action_tab() {
      return this.detail_tabs.find((tab) => String(tab?.badge || '').trim().toLowerCase() === 'current') || this.detail_tabs[0] || null;
    },
    current_detail_action_copy() {
      return 'Check submitted evidence, preview missing or incorrect documents, and decide whether to advance or request a re-upload.';
    },
    scroll_detail_tabs(direction = 'right') {
      const tab_scroller = this.$refs.detailTabsScroller;
      if (!tab_scroller) return;

      const scroll_offset = Math.max(180, Math.round(tab_scroller.clientWidth * 0.6));
      const left = direction === 'left'
        ? tab_scroller.scrollLeft - scroll_offset
        : tab_scroller.scrollLeft + scroll_offset;

      tab_scroller.scrollTo({
        left,
        behavior: 'smooth'
      });
    },
    update_detail_tab_overflow() {
      const tab_scroller = this.$refs.detailTabsScroller;
      if (!tab_scroller) {
        this.detail_tab_overflow = { left: false, right: false };
        return;
      }

      const max_scroll_left = Math.max(0, tab_scroller.scrollWidth - tab_scroller.clientWidth);
      this.detail_tab_overflow = {
        left: tab_scroller.scrollLeft > 8,
        right: tab_scroller.scrollLeft < max_scroll_left - 8
      };
    },
    scroll_active_detail_tab_into_view() {
      const tab_scroller = this.$refs.detailTabsScroller;
      const active_tab = tab_scroller?.querySelector?.(`[data-detail-tab-id="${this.active_detail_tab}"]`);

      if (!tab_scroller || !active_tab) return;

      active_tab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    status_badge_class(tone) {
      if (tone === 'danger') return 'bg-danger-subtle text-danger-emphasis';
      if (tone === 'success') return 'bg-success-subtle text-success-emphasis';
      if (tone === 'warning') return 'bg-warning-subtle text-warning-emphasis';
      return 'bg-secondary-subtle text-secondary-emphasis';
    },
    activation_badge_class() {
      return this.selected_document?.case_item?.activation_tone === 'ready'
        ? 'bg-success-subtle text-success-emphasis'
        : 'bg-warning-subtle text-warning-emphasis';
    },
    detail_journey_label() {
      return first_value([
        this.selected_document?.type,
        this.selected_document?.case_item?.journey
      ], 'Onboarding');
    },
    detail_engagement_label() {
      return first_value([
        this.selected_document?.case_item?.engagement,
        this.selected_document?.case_item?.engagement_value,
        this.selected_document?.case_item?.contract_value,
        this.selected_document?.case_item?.amount
      ], 'Not provided');
    },
    detail_owner_label() {
      return first_value([
        this.selected_document?.reviewer,
        this.selected_document?.case_item?.owner
      ], 'Unassigned');
    },
    detail_submitted_label() {
      return this.format_detail_date(first_value([
        this.selected_document?.case_item?.submitted_at,
        this.selected_document?.case_item?.created_at,
        this.selected_document?.case_item?.updated_at
      ], ''));
    },
    detail_activation_means_label() {
      return first_value([
        this.selected_document?.case_item?.activation_means,
        this.selected_document?.case_item?.activation_description,
        this.selected_document?.case_item?.detail
      ], 'Activation details not provided');
    },
    detail_progress_bar_color() {
      return this.selected_document?.case_item?.progress_tone === 'success' ? '#198754' : '#bf4b08';
    },
    is_document_question(question = {}) {
      const label = normalize_text(question?.label || '');
      const answer = String(question?.answer || '').trim();
      return String(question?.type || '').trim().toLowerCase() === 'file'
        || Boolean(String(question?.file_url || '').trim())
        || /^(https?:\/\/|www\.)/i.test(answer)
        || DOCUMENT_QUESTION_WORDS.some((word) => label.includes(word));
    },
    document_answer_meta(question = {}) {
      const raw_answer = String(question?.answer || '').trim();
      const file_url = String(question?.file_url || '').trim();
      const file_name = String(question?.file_name || '').trim();
      const uploaded = Boolean(file_url || file_name || raw_answer)
        && !MISSING_DOCUMENT_ANSWERS.includes(raw_answer.toLowerCase());

      return {
        name: file_name || raw_answer || 'Not uploaded',
        url: file_url || (/^(https?:\/\/|www\.)/i.test(raw_answer) ? raw_answer : ''),
        uploaded
      };
    },
    document_status_meta(question = {}) {
      const document_meta = this.document_answer_meta(question);
      if (document_meta.uploaded) {
        return {
          label: 'Uploaded',
          pill_class: 'bg-success-subtle text-success-emphasis',
          icon_class: 'onboarding-document-status-icon is-success',
          icon: 'bi-check-lg'
        };
      }

      return {
        label: 'Missing',
        pill_class: 'bg-danger-subtle text-danger-emphasis',
        icon_class: 'onboarding-document-status-icon is-danger',
        icon: 'bi-x-lg'
      };
    },
    open_question_document(question, event) {
      event?.stopPropagation?.();
      const document_meta = this.document_answer_meta(question);
      if (!document_meta.url) return;

      this.open_document_preview({
        document: question?.label || 'Uploaded document',
        file_name: document_meta.name,
        file_url: document_meta.url
      });
    }
  }
};
